import { PublicClientApplication, EventType, AccountInfo } from '@azure/msal-browser';
import { msalConfig } from '../config/authConfig';
import { apiService } from './apiService';
import { msalLogger } from '../utils/logger';
import { AuthEventPayload } from '../types/window';
import { getDynamicRedirectUri } from '../utils/authUtils';
import { recordNativeDiagnostic } from '../utils/nativeDiagnostics';
import { MSAL_SERVICE } from '../config/defaults';

class MsalService {
  private instance: PublicClientApplication | null = null;
  private initialized = false;
  private redirectHandled = false;
  private initializationPromise: Promise<void> | null = null;

  private readonly MAX_RETRIES = MSAL_SERVICE.MAX_RETRIES;
  private readonly RETRY_DELAY = MSAL_SERVICE.RETRY_DELAY;

  constructor() {
    // Check for existing instance on instantiation
    if (window.__MSAL_INSTANCE__) {
      msalLogger.debug('Reusing existing MSAL instance from window');
      this.instance = window.__MSAL_INSTANCE__;
      this.initialized = window.__MSAL_INITIALIZED__ || false;
      this.redirectHandled = window.__MSAL_REDIRECT_HANDLED__ || false;
    }
  }

  getInstance(): PublicClientApplication {
    // Always check window first to prevent duplication
    if (window.__MSAL_INSTANCE__) {
      msalLogger.debug('Using existing MSAL instance from window');
      this.instance = window.__MSAL_INSTANCE__;
      return this.instance;
    }

    if (!this.instance) {
      msalLogger.debug('Creating new MSAL instance with dynamic redirect URI');
      
      // Create a fresh config with current domain's redirect URI
      const dynamicConfig = {
        ...msalConfig,
        auth: {
          ...msalConfig.auth,
          redirectUri: getDynamicRedirectUri(),
          postLogoutRedirectUri: getDynamicRedirectUri()
        }
      };
      
      msalLogger.debug('MSAL instance configuration:', {
        clientId: dynamicConfig.auth.clientId,
        redirectUri: dynamicConfig.auth.redirectUri,
        authority: dynamicConfig.auth.authority
      });
      
      this.instance = new PublicClientApplication(dynamicConfig);
      
      // Immediately store in window to prevent duplication
      window.__MSAL_INSTANCE__ = this.instance;
      msalLogger.debug('Stored MSAL instance in window');
    }
    return this.instance;
  }

  async initialize(): Promise<void> {
    // Return existing promise if initialization is in progress
    if (this.initializationPromise) {
      msalLogger.debug('MSAL initialization already in progress, waiting...');
      return this.initializationPromise;
    }

    // Check if already initialized globally
    if (window.__MSAL_INITIALIZED__ && window.__MSAL_INSTANCE__) {
      msalLogger.debug('MSAL already initialized globally, syncing local state...');
      this.instance = window.__MSAL_INSTANCE__;
      this.initialized = true;
      this.redirectHandled = window.__MSAL_REDIRECT_HANDLED__ || false;
      
      // Ensure apiService is initialized with the existing instance
      if (this.instance) {
        apiService.initialize(this.instance);
      }
      return;
    }

    // Check if this service instance is already initialized
    if (this.initialized) {
      msalLogger.debug('This MSAL service instance already initialized, skipping...');
      return;
    }

    msalLogger.debug('Starting MSAL initialization...');
    this.initializationPromise = this.initializeWithRetry();
    
    try {
      await this.initializationPromise;
    } finally {
      // Clear the promise after completion (success or failure)
      this.initializationPromise = null;
    }
  }

  private async initializeWithRetry(): Promise<void> {
    for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        await this.performInitialization();
        return;
      } catch (error) {
        msalLogger.error(`MSAL initialization attempt ${attempt} failed:`, error);
        
        // Check if this is a critical error that requires immediate force reauthentication
        if (this.isCriticalAuthError(error)) {
          msalLogger.warn('Critical auth error detected during initialization, forcing reauthentication...');
          this.initializationPromise = null;
          await this.forceReauthentication();
          return; // Exit after forcing reauthentication
        }
        
        if (attempt === this.MAX_RETRIES) {
          this.initializationPromise = null;
          const errorMessage = error instanceof Error ? error.message : String(error);
          throw new Error(`MSAL initialization failed after ${this.MAX_RETRIES} attempts: ${errorMessage}`);
        }
        
        // Wait before retrying for non-critical errors
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * attempt));
      }
    }
  }

  private async performInitialization(): Promise<void> {
    // Double-check if already initialized to prevent race conditions
    if (window.__MSAL_INITIALIZED__ && window.__MSAL_INSTANCE__) {
      msalLogger.debug('MSAL already initialized during initialization attempt, aborting...');
      this.instance = window.__MSAL_INSTANCE__;
      this.initialized = true;
      this.redirectHandled = window.__MSAL_REDIRECT_HANDLED__ || false;
      return;
    }

    const instance = this.getInstance();
    
    // Only initialize if not already initialized
    if (!window.__MSAL_INSTANCE_INITIALIZED__) {
      msalLogger.debug('Initializing MSAL instance...');
      await instance.initialize();
      window.__MSAL_INSTANCE_INITIALIZED__ = true;
    }
    
    // Handle any pending redirect promises only once
    if (!this.redirectHandled && !window.__MSAL_REDIRECT_HANDLED__) {
      msalLogger.debug('Handling redirect promise...');
      recordNativeDiagnostic('MSAL handleRedirectPromise start', {
        path: window.location.pathname,
        hasQueryCode: window.location.search.includes('code='),
        hasHashCode: window.location.hash.includes('code=')
      });
      try {
        const response = await instance.handleRedirectPromise();
        if (response) {
          msalLogger.debug('Redirect response received');
          recordNativeDiagnostic('MSAL redirect response received', {
            account: response.account?.username
          });
        } else {
          msalLogger.debug('No redirect response received');
          recordNativeDiagnostic('MSAL redirect response: none');
        }
        this.redirectHandled = true;
        window.__MSAL_REDIRECT_HANDLED__ = true;
      } catch (error) {
        msalLogger.error('Error handling redirect promise:', error);
        recordNativeDiagnostic('MSAL redirect error', error);
        this.redirectHandled = true;
        window.__MSAL_REDIRECT_HANDLED__ = true;
        
        // Check if this is a critical auth error that requires force reauthentication
        if (this.isCriticalAuthError(error)) {
          msalLogger.warn('Critical auth error detected, forcing reauthentication...');
          await this.forceReauthentication();
          return; // Exit early after forcing reauthentication
        }
        
        throw error;
      }
    }
    
    this.initialized = true;
    window.__MSAL_INITIALIZED__ = true;

    // Initialize apiService with MSAL instance
    apiService.initialize(instance);

    // Set active account after initialization
    this.setActiveAccount();

    recordNativeDiagnostic('MSAL initialized', {
      accounts: instance.getAllAccounts().length,
      activeAccount: instance.getActiveAccount()?.username
    });

    // Add event callback after initialization
    this.setupEventCallbacks();

    // Dispatch initialization event
    this.dispatchInitializationEvent();

    msalLogger.debug('MSAL initialization completed successfully');
  }

  /**
   * Check if the error is a critical authentication error that requires force reauthentication
   */
  private isCriticalAuthError(error: any): boolean {
    if (!error || typeof error !== 'object') return false;
    
    const errorMessage = error.message || error.errorMessage || '';
    const errorCode = error.errorCode || error.error || '';
    
    // List of critical error codes/messages that require force reauthentication
    const criticalErrors = [
      'AADSTS40008', // External identity provider error
      'AADSTS50166', // Request to External OIDC endpoint failed
      'AADSTS50058', // Silent sign-in request failed
      'AADSTS50079', // User needs to use multi-factor authentication
      'AADSTS50076', // User needs to use multi-factor authentication (different code)
      'AADSTS700082', // Expired refresh token
      'AADSTS700084', // Refresh token not found
      'AADSTS50020', // User account from identity provider does not exist
      'AADSTS65001', // The user or administrator has not consented
      'AADSTS65004', // User declined to consent to access the app
      'interaction_required',
      'login_required',
      'consent_required'
    ];
    
    const isCritical = criticalErrors.some(criticalError => 
      errorMessage.includes(criticalError) || errorCode.includes(criticalError)
    );
    
    if (isCritical) {
      msalLogger.warn('Critical auth error detected:', {
        errorCode,
        errorMessage: errorMessage.substring(0, 100),
        fullError: error
      });

      // Handle OIDC endpoint failures with specialized recovery
      if (errorMessage.includes('AADSTS50166') || errorCode.includes('AADSTS50166')) {
        msalLogger.warn('OIDC endpoint failure detected, using specialized recovery');
        setTimeout(() => this.handleOIDCEndpointFailure(), 100);
        return false; // Don't use regular force reauth for this
      }
    }
    
    return isCritical;
  }

  /**
   * Force reauthentication by clearing all cache and redirecting to login
   */
  private async forceReauthentication(): Promise<void> {
    try {
      msalLogger.warn('Starting force reauthentication process...');
      
      if (this.instance) {
        // Clear all accounts from cache
        const accounts = this.instance.getAllAccounts();
        if (accounts.length > 0) {
          msalLogger.debug('Clearing accounts from cache:', accounts.map(a => a.username));
        }
        
        // Clear active account
        this.instance.setActiveAccount(null);
        
        // Clear browser cache
        await this.instance.clearCache();
        msalLogger.debug('MSAL cache cleared');
      }
      
      // Clear session storage
      try {
        sessionStorage.clear();
        msalLogger.debug('Session storage cleared');
      } catch (e) {
        msalLogger.warn('Could not clear session storage:', e);
      }
      
      // Also clear local storage to be thorough
      try {
        // Only clear MSAL-related items to avoid clearing other app data
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('msal.') || key.includes('msal') || key.includes('auth'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        msalLogger.debug('MSAL-related local storage cleared');
      } catch (e) {
        msalLogger.warn('Could not clear local storage:', e);
      }
      
      // Clear our global state
      this.clearGlobalState();
      
      // Reset local state
      this.initialized = false;
      this.redirectHandled = false;
      this.initializationPromise = null;
      
      msalLogger.warn('Force reauthentication cleanup completed, initiating fresh login...');
      
      // Try multiple approaches to ensure fresh authentication
      this.initiateForceLogin();
      
    } catch (error) {
      msalLogger.error('Error during force reauthentication:', error);
      // As a last resort, reload the page
      window.location.reload();
    }
  }

  /**
   * Initiate force login with multiple fallback strategies
   */
  private initiateForceLogin(): void {
    // Small delay to ensure cleanup is complete, then force login
    setTimeout(async () => {
      try {
        // Strategy 1: Try loginRedirect with prompt=login
        await this.tryLoginRedirect();
      } catch (loginError) {
        msalLogger.warn('Login redirect failed, trying direct Azure AD URL:', loginError);
        
        try {
          // Strategy 2: Direct Azure AD URL with prompt=login
          await this.tryDirectAzureLogin();
        } catch (directError) {
          msalLogger.warn('Direct Azure login failed, trying basic OAuth flow:', directError);
          
          try {
            // Strategy 3: Basic OAuth2 flow for OIDC issues
            await this.tryBasicOAuthFlow();
          } catch (basicError) {
            msalLogger.error('All login strategies failed, falling back to page reload:', basicError);
            // Strategy 4: Fallback to page reload
            window.location.reload();
          }
        }
      }
    }, 500);
  }

  /**
   * Try standard login redirect with forced prompt
   */
  private async tryLoginRedirect(): Promise<void> {
    // Get a fresh instance for login
    const freshInstance = this.getInstance();
    
    // Import login request config
    const { loginRequest } = await import('../config/authConfig');
    
    // Create a forced login request with prompt=login to ensure fresh auth
    const forcedLoginRequest = {
      ...loginRequest,
      prompt: 'login' as const, // Force user to enter credentials again
      extraQueryParameters: {
        force_reauth: 'true' // Custom parameter for tracking
      }
    };
    
    // Force a fresh login redirect
    msalLogger.warn('Initiating forced login redirect with prompt=login');
    await freshInstance.loginRedirect(forcedLoginRequest);
  }

  /**
   * Try direct Azure AD login URL
   */
  private async tryDirectAzureLogin(): Promise<void> {
    const config = this.instance?.getConfiguration();
    if (!config) {
      throw new Error('No MSAL configuration available');
    }

    const clientId = config.auth.clientId;
    const authority = config.auth.authority;
    const redirectUri = encodeURIComponent(getDynamicRedirectUri());
    
    // For AADSTS50166 errors, try a simpler auth flow without custom parameters
    const loginUrl = `${authority}/oauth2/v2.0/authorize?` +
      `client_id=${clientId}&` +
      `response_type=code&` +
      `redirect_uri=${redirectUri}&` +
      `scope=openid%20profile%20email&` +
      `prompt=login`;

    msalLogger.warn('Redirecting to direct Azure AD login URL (simplified for OIDC issues)');
    window.location.href = loginUrl;
  }

  /**
   * Alternative login method for OIDC endpoint failures
   * Uses a more basic OAuth2 flow
   */
  private async tryBasicOAuthFlow(): Promise<void> {
    const config = this.instance?.getConfiguration();
    if (!config) {
      throw new Error('No MSAL configuration available');
    }

    const clientId = config.auth.clientId;
    const authority = config.auth.authority;
    const redirectUri = encodeURIComponent(getDynamicRedirectUri());
    
    // Very basic OAuth2 flow without OIDC extensions
    const basicLoginUrl = `${authority}/oauth2/v2.0/authorize?` +
      `client_id=${clientId}&` +
      `response_type=code&` +
      `redirect_uri=${redirectUri}&` +
      `scope=openid&` +
      `prompt=login&` +
      `response_mode=query`; // Use query instead of fragment for better compatibility

    msalLogger.warn('Trying basic OAuth2 flow for OIDC endpoint issues');
    window.location.href = basicLoginUrl;
  }

  /**
   * Clear global MSAL state
   */
  private clearGlobalState(): void {
    try {
      delete window.__MSAL_INSTANCE__;
      delete window.__MSAL_INITIALIZED__;
      delete window.__MSAL_REDIRECT_HANDLED__;
      delete window.__MSAL_INSTANCE_INITIALIZED__;
      msalLogger.debug('Global MSAL state cleared');
    } catch (error) {
      msalLogger.warn('Error clearing global state:', error);
    }
  }

  private setActiveAccount(): void {
    if (!this.instance) return;

    const accounts = this.instance.getAllAccounts();
    if (!this.instance.getActiveAccount() && accounts.length > 0) {
      msalLogger.debug('Setting active account:', accounts[0].username);
      this.instance.setActiveAccount(accounts[0]);
    }
  }

  private setupEventCallbacks(): void {
    if (!this.instance) return;

    this.instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        msalLogger.debug('Login success event received');
        const payload = event.payload as AuthEventPayload;
        if (payload.account && this.instance) {
          this.instance.setActiveAccount(payload.account);
        }
      }
    });
  }

  private dispatchInitializationEvent(): void {
    msalLogger.debug('Dispatching msalInitialized event');
    const event = new CustomEvent('msalInitialized');
    window.dispatchEvent(event);
  }

  isInitialized(): boolean {
    // Check both local state and global state
    const globallyInitialized = !!(window.__MSAL_INITIALIZED__ && window.__MSAL_INSTANCE__);
    const locallyInitialized = !!(this.initialized && this.instance);
    
    // If globally initialized but not locally, sync the state
    if (globallyInitialized && !locallyInitialized) {
      msalLogger.debug('Syncing local state with global MSAL state');
      this.instance = window.__MSAL_INSTANCE__!;
      this.initialized = true;
    }
    
    return globallyInitialized || locallyInitialized;
  }

  getAccounts(): AccountInfo[] {
    return this.instance?.getAllAccounts() || [];
  }

  getActiveAccount(): AccountInfo | null {
    return this.instance?.getActiveAccount() || null;
  }

  /**
   * Get the current redirect URI being used by the MSAL instance
   * Useful for debugging and verification
   */
  getCurrentRedirectUri(): string {
    return getDynamicRedirectUri();
  }

  /**
   * Get configuration info for debugging
   */
  getConfigInfo(): { redirectUri: string; authority: string; clientId: string } {
    const config = this.instance?.getConfiguration();
    return {
      redirectUri: this.getCurrentRedirectUri(),
      authority: config?.auth.authority || 'unknown',
      clientId: config?.auth.clientId || 'unknown'
    };
  }

  /**
   * Public method to force reauthentication - can be called when auth errors are detected
   */
  async forceReauthenticationPublic(): Promise<void> {
    msalLogger.warn('Force reauthentication requested publicly');
    await this.forceReauthentication();
  }

  /**
   * Immediate force login without cleanup - for when you just want to force a fresh login
   */
  async immediateForceLogin(): Promise<void> {
    msalLogger.warn('Immediate force login requested');
    try {
      const freshInstance = this.getInstance();
      const { loginRequest } = await import('../config/authConfig');
      
      const forcedLoginRequest = {
        ...loginRequest,
        prompt: 'login' as const,
        extraQueryParameters: {
          force_reauth: 'true'
        }
      };
      
      await freshInstance.loginRedirect(forcedLoginRequest);
    } catch (error) {
      msalLogger.error('Immediate force login failed, trying direct Azure URL:', error);
      await this.tryDirectAzureLogin();
    }
  }

  /**
   * Handle OIDC endpoint failures specifically (AADSTS50166)
   * Uses a simplified OAuth2 flow that avoids problematic OIDC extensions
   */
  async handleOIDCEndpointFailure(): Promise<void> {
    msalLogger.warn('Handling OIDC endpoint failure (AADSTS50166)');
    
    try {
      // Clear cache first to avoid any corrupted state
      if (this.instance) {
        await this.instance.clearCache();
        msalLogger.debug('Cache cleared for OIDC endpoint failure handling');
      }
      
      // Clear storage
      try {
        sessionStorage.clear();
        localStorage.removeItem('msal.interaction.status');
        localStorage.removeItem('msal.request.params');
      } catch (e) {
        msalLogger.warn('Could not clear storage:', e);
      }
      
      // Try basic OAuth2 flow immediately
      await this.tryBasicOAuthFlow();
      
    } catch (error) {
      msalLogger.error('OIDC endpoint failure handling failed, falling back to page reload:', error);
      // Last resort: reload page
      window.location.reload();
    }
  }

  /**
   * Check URL parameters for authentication errors and handle them
   * This catches errors that might appear in URL fragments or query parameters
   */
  checkUrlForAuthErrors(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const urlHash = window.location.hash;
    
    // Check for error in URL parameters
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    // Check for error in URL hash (fragment)
    let hashError = null;
    let hashErrorDescription = null;
    if (urlHash) {
      const hashParams = new URLSearchParams(urlHash.substring(1));
      hashError = hashParams.get('error');
      hashErrorDescription = hashParams.get('error_description');
    }
    
    const error = errorParam || hashError;
    const description = errorDescription || hashErrorDescription;
    
    if (error || (description && description.includes('AADSTS'))) {
      msalLogger.error('Authentication error found in URL:', {
        error,
        description: description?.substring(0, 200),
        currentUrl: window.location.href
      });
      
      // Handle OIDC endpoint failures specifically
      if (description?.includes('AADSTS50166')) {
        msalLogger.warn('OIDC endpoint failure detected in URL, handling...');
        this.handleOIDCEndpointFailure();
        return true;
      }
      
      // Handle other critical errors
      if (this.isCriticalAuthError({ message: description, errorCode: error })) {
        msalLogger.warn('Critical auth error detected in URL, forcing reauthentication...');
        this.forceReauthentication();
        return true;
      }
    }
    
    return false;
  }

  /**
   * Check if an error requires force reauthentication (public method)
   */
  shouldForceReauthentication(error: any): boolean {
    return this.isCriticalAuthError(error);
  }
}
let msalServiceInstance: MsalService | null = null;

function createMsalService(): MsalService {
  if (msalServiceInstance) {
    msalLogger.debug('Returning existing MsalService instance');
    return msalServiceInstance;
  }
  
  msalLogger.debug('Creating new MsalService instance');
  msalServiceInstance = new MsalService();
  return msalServiceInstance;
}

export const msalService = createMsalService();
